import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Stack, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import { COMMANDS, toCommandBody } from '../deviceHub/deviceCommands';
import { statusSx } from '../../config/platformModules';
import RunLogPanel from '../fieldkit/RunLogPanel';

// 기기 상세 (결정 ⑥ · 12.01-D008, docs/V1_ADMIN_SCREEN_REVIEW.md §5-2) — 값마다 출처 표기 (M8)
const TABS = [
  { id: 'overview', label: '개요' },
  { id: 'commands', label: '명령' },
  { id: 'logs', label: '로그' },
  { id: 'remote', label: '원격' },
  { id: 'wg', label: 'WG' },
  { id: 'field', label: '현장 기록' },
];

const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const ago = (sec) => (sec == null ? '—' : sec < 90 ? `${sec}초 전` : sec < 5400 ? `${Math.round(sec / 60)}분 전` : `${Math.round(sec / 3600)}시간 전`);
const Source = ({ s }) => <Chip size="small" variant="outlined" label={s} sx={{ height: 18, fontSize: 10, ml: 0.5 }} />;

function Row({ label, children, source }) {
  return (
    <Box sx={{ display: 'flex', py: 0.75, borderBottom: '1px solid', borderColor: 'divider', gap: 2 }}>
      <Typography sx={{ width: 150, flexShrink: 0, color: 'text.secondary', fontSize: 13 }}>{label}</Typography>
      <Box sx={{ fontSize: 14, minWidth: 0, wordBreak: 'break-all' }}>{children}{source && <Source s={source} />}</Box>
    </Box>
  );
}

function DeviceDetail({ user }) {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.id === params.get('tab'))?.id || 'overview';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/peer/devices/${encodeURIComponent(code)}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
    setLoading(false);
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  const d = data?.device;
  const st = data?.status;
  const tunnel = st?.tunnel;

  return (
    <Layout title="Devices" user={user}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1, sm: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
          <Button size="small" component={RouterLink} to="/devices" startIcon={<ArrowBackIcon />}>목록</Button>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{d?.name || code}</Typography>
          <Typography sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{code}</Typography>
          {st && <Chip size="small" sx={statusSx(st.socket.online ? 'ok' : 'critical')} label={st.socket.online ? '앱 접속 중' : '앱 오프라인'} />}
          {tunnel && <Chip size="small" sx={statusSx(tunnel.connected ? 'ok' : 'warn')} label={tunnel.connected ? 'WG 연결' : 'WG 끊김'} />}
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>새로고침</Button>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setParams({ tab: v }, { replace: true })} variant="scrollable" allowScrollButtonsMobile
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          {TABS.map((t) => <Tab key={t.id} value={t.id} label={t.label} />)}
        </Tabs>

        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            {tab === 'overview' && <Overview d={d} st={st} />}
            {tab === 'commands' && <Commands deviceCode={d.deviceCode} />}
            {tab === 'logs' && <Logs deviceCode={d.deviceCode} appVersion={d.appVersion} />}
            {tab === 'remote' && <Remote st={st} />}
            {tab === 'wg' && <Wg st={st} />}
            {tab === 'field' && <Field data={data} />}
            {tab === 'field' && (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>원클릭 실행 기록 (단계·로그)</Typography>
                <RunLogPanel deviceCode={d.deviceCode} embedded />
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Layout>
  );
}

function Overview({ d, st }) {
  const t = st.tunnel;
  return (
    <Box>
      <Row label="매장">{d.store ? `${d.store.name}${d.store.tljNo ? ` (TLJ ${d.store.tljNo})` : ''}` : '미배정'}</Row>
      <Row label="등록 상태" source="db">{d.registrationStatus || '—'}</Row>
      <Row label="앱 버전" source="db">{d.appVersion || '—'}</Row>
      <Row label="칩셋 / MAC" source="db">{d.chipset || '—'} · {d.hwMac || '—'}</Row>
      <Row label="앱 접속" source="socket">{st.socket.online ? '접속 중' : '오프라인'}</Row>
      <Row label="WG 실제 연결" source="wg-server">
        {t ? `${t.connected ? '연결' : '끊김'} · 마지막 핸드셰이크 ${ago(t.handshakeAgeSec)}` : 'wg-server 에 피어 없음 (또는 MAC 미기록)'}
      </Row>
      <Row label="WG (DB 기록)" source="db">{st.wgDb.status} · {st.wgDb.ip || '—'} <Typography component="span" variant="caption" color="text.secondary">— 프로비저닝 시점 값</Typography></Row>
      {st.telemetry && <Row label="텔레메트리" source="telemetry">{fmt(st.telemetry.updatedAt)}</Row>}
    </Box>
  );
}

function Commands({ deviceCode }) {
  const [history, setHistory] = useState([]);
  const [cmd, setCmd] = useState(COMMANDS[0].value);
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => {
    try {
      const res = await api.get(`/peer/command/history/${deviceCode}?limit=20`);
      setHistory(res.data.commands || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load history');
    }
  }, [deviceCode]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    setSending(true);
    try {
      await api.post('/peer/command/send', { deviceCode, ...toCommandBody(cmd) });
      toast.success('Sent');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
    setSending(false);
  };
  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField select size="small" value={cmd} onChange={(e) => setCmd(e.target.value)} sx={{ minWidth: 320 }}>
          {COMMANDS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        <Button variant="contained" disabled={sending} onClick={send}>명령 보내기</Button>
      </Stack>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 560, '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead><TableRow><TableCell>시각</TableCell><TableCell>명령</TableCell><TableCell>상태</TableCell><TableCell>결과</TableCell></TableRow></TableHead>
          <TableBody>
            {(!Array.isArray(history) || history.length === 0) && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>명령 기록 없음</TableCell></TableRow>
            )}
            {Array.isArray(history) && history.map((h) => (
              <TableRow key={h._id}>
                <TableCell>{fmt(h.createdAt)}</TableCell>
                <TableCell>{h.command}</TableCell>
                <TableCell>{h.status}</TableCell>
                <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.result ? JSON.stringify(h.result) : h.error || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// 진단 로그 — 요청할 때만 기기가 보냄 (2.13.6+, 최대 32KB). 기기당 최근 20개·14일 보관
function Logs({ deviceCode, appVersion }) {
  const [logs, setLogs] = useState([]);
  const [sel, setSel] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const load = useCallback(async () => {
    const res = await api.get(`/peer/devices/${encodeURIComponent(deviceCode)}/logs`);
    setLogs(res.data.logs || []);
    return res.data.logs || [];
  }, [deviceCode]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const request = async () => {
    setWaiting(true);
    try {
      const before = logs[0]?._id;
      await api.post('/peer/command/send', { deviceCode, command: 'collect_logs', payload: {} });
      // 기기가 보통 몇 초 안에 보냄 — 3초 간격으로 30초까지 확인
      for (let i = 0; i < 10; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        const next = await load();
        if (next[0] && next[0]._id !== before) { setSel(0); toast.success('로그 받음'); setWaiting(false); return; }
      }
      toast.error('30초 안에 로그가 오지 않았습니다 — 앱 2.13.6 이상인지, 앱이 접속 중인지 확인하세요');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
    setWaiting(false);
  };
  const cur = logs[sel];
  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Button variant="contained" disabled={waiting} onClick={request} startIcon={waiting ? <CircularProgress size={16} /> : null}>
          {waiting ? '기다리는 중…' : '로그 요청'}
        </Button>
        {logs.length > 0 && (
          <TextField select size="small" value={sel} onChange={(e) => setSel(Number(e.target.value))} sx={{ minWidth: 260 }}>
            {logs.map((l, i) => <MenuItem key={l._id} value={i}>{fmt(l.createdAt)} · {l.appVersion || '?'} · {Math.max(1, Math.ceil((l.bytes || 0) / 1024))}KB</MenuItem>)}
          </TextField>
        )}
        <Typography variant="caption" color="text.secondary">현재 앱 {appVersion || '—'} · 2.13.6 이상에서 동작</Typography>
      </Stack>
      {cur ? (
        <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 1, fontSize: 11, lineHeight: 1.45, maxHeight: 520, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {cur.logs}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">받은 로그 없음 — "로그 요청"을 누르면 기기 상태 요약과 앱 최근 로그를 받습니다.</Typography>
      )}
    </Box>
  );
}

function Remote({ st }) {
  // 원격 ADB 는 tools.digntec.com 에서 (CMS 서버에는 adb 가 없음) — docs/REMOTE_SUPPORT_RUNBOOK.md
  const wgIp = st?.tunnel?.ip || st?.wgDb?.ip;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        1단계(앱 재시작·캐시·스크린샷·로그)는 "명령"·"로그" 탭에서. 깊은 진단(쉘·설치)은 tools.digntec.com 에서 WG IP 로 연결합니다 —
        WG 탭의 핸드셰이크가 2분 이내일 때만 됩니다. 작업 후 연결 해제.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>연결 주소: <b>{wgIp ? `${wgIp}:5555` : 'WG IP 없음 — 현장 키트로 WG 설정 필요'}</b></Typography>
      <Button variant="outlined" href="https://tools.digntec.com" target="_blank" rel="noopener">tools.digntec.com 열기</Button>
    </Box>
  );
}

function Wg({ st }) {
  const t = st.tunnel;
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>실제 터널 <Source s="wg-server" /></Typography>
      {t ? (
        <>
          <Row label="연결">{t.connected ? '연결 (핸드셰이크 3분 이내)' : t.everConnected ? '끊김 (예전에 연결된 적 있음)' : '한 번도 연결 안 됨'}</Row>
          <Row label="마지막 핸드셰이크">{t.lastHandshakeAt ? `${fmt(t.lastHandshakeAt)} (${ago(t.handshakeAgeSec)})` : '—'}</Row>
          <Row label="WG IP">{t.ip || '—'}</Row>
          <Row label="기기 쪽 주소">{t.endpoint || '—'}</Row>
          <Row label="송수신">{`${Math.round((t.rxBytes || 0) / 1024)} KB 받음 · ${Math.round((t.txBytes || 0) / 1024)} KB 보냄`}</Row>
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>wg-server 에 이 기기 피어가 없습니다 (MAC 미기록이거나 WG 미설정). 현장 키트 원클릭 + 현장 코드로 설정할 수 있습니다.</Alert>
      )}
      <Typography sx={{ fontWeight: 700, mt: 2, mb: 1 }}>DB 기록 <Source s="db" /></Typography>
      <Row label="wgStatus">{st.wgDb.status}</Row>
      <Row label="WG IP">{st.wgDb.ip || '—'}</Row>
      <Row label="프로비저닝">{st.wgDb.provisioningStatus || '—'} · {fmt(st.wgDb.provisionedAt)} · {st.wgDb.provisionedBy || '—'}</Row>
      <Typography variant="caption" color="text.secondary">DB 값은 프로비저닝할 때 저장된 값이라 실제 연결 여부와 다를 수 있습니다.</Typography>
    </Box>
  );
}

function Field({ data }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>현장 키트 실행 기록 <Source s="field-kit" /></Typography>
      <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small" sx={{ minWidth: 560, '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead><TableRow><TableCell>시각</TableCell><TableCell>앱</TableCell><TableCell>설치</TableCell><TableCell>매장 메모</TableCell><TableCell>출처</TableCell></TableRow></TableHead>
          <TableBody>
            {data.fieldReports.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>기록 없음</TableCell></TableRow>}
            {data.fieldReports.map((r) => (
              <TableRow key={r._id}>
                <TableCell>{fmt(r.createdAt)}</TableCell>
                <TableCell>{r.appVersion || r.apkInstalledVersion || '—'}</TableCell>
                <TableCell>{r.installOk ? 'OK' : '실패'}</TableCell>
                <TableCell>{r.storeLabel || '—'}</TableCell>
                <TableCell>{r.source || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>현장 코드로 WG 설치</Typography>
      {data.fieldCodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">없음</Typography>
      ) : data.fieldCodes.map((c, i) => (
        <Row key={i} label={fmt(c.createdAt)}>
          {c.storeLabel || '—'} · 발급 {c.issuedBy || '—'} · {c.provisions.map((p) => `${p.wgIp || '?'} (${fmt(p.at)})`).join(', ')}
        </Row>
      ))}
    </Box>
  );
}

export default DeviceDetail;
